import {
    LinkedAccountWithMetadata,
    usePrivy,
    useWallets,
    WalletWithMetadata,
} from '@privy-io/react-auth'
import { useMemo } from 'react'
import { getSigner } from './getSigner'

/**
 * @returns A function to get the signer for the wallet linked to the user's privy account
 * Important: Our privy configuration should allow for a single wallet to be linked to the user's privy account
 * https://docs.privy.io/basics/get-started/dashboard/configure-login-methods#restrict-users-to-a-single-wallet
 *
 * "You can enable the Restrict users to linking a single third-party wallet option for your application.
 * When enabled, users can only link one wallet to their account, preventing potential confusion or security issues that might arise from multiple linked wallets."
 */
export function useGetSignUpWalletSigner() {
    const { user, ready } = usePrivy()
    const { wallets, ready: walletsReady } = useWallets()
    return useMemo(() => {
        const signUpWalletMetadata = user?.linkedAccounts.find(isSignUpWallet)
        const signUpWallet = wallets.find(
            (wallet) => wallet.address === signUpWalletMetadata?.address,
        )
        const _ready = ready && walletsReady

        return {
            ready: _ready,
            getSigner: (chainId: number, enableDebugLogging = false) => {
                if (!_ready) {
                    console.log('[useGetSignUpWalletSigner] getSignerCalled but not ready', {
                        ready,
                        walletsReady,
                    })
                    return
                }

                // Debug logging when ready but no sign-up wallet found
                if (enableDebugLogging && !signUpWallet && user?.linkedAccounts) {
                    console.log('[useGetSignUpWalletSigner] Debug info:', {
                        linkedAccountsCount: user.linkedAccounts.length,
                        linkedAccounts: user.linkedAccounts.map((acc) => ({
                            type: acc.type,
                            connectorType: 'connectorType' in acc ? acc.connectorType : undefined,
                            walletClientType:
                                'walletClientType' in acc ? acc.walletClientType : undefined,
                            address: 'address' in acc ? acc.address : undefined,
                        })),
                        walletsCount: wallets.length,
                        wallets: wallets.map((w) => ({
                            address: w.address,
                            connectorType: w.connectorType,
                            walletClientType: w.walletClientType,
                        })),
                        signUpWalletMetadata: signUpWalletMetadata
                            ? {
                                  type: signUpWalletMetadata.type,
                                  address: signUpWalletMetadata.address,
                                  connectorType: signUpWalletMetadata.connectorType,
                              }
                            : null,
                    })
                }
                return getSigner(signUpWallet, chainId)
            },
        }
    }, [user, ready, wallets, walletsReady])
}

export function isSignUpWallet(account: LinkedAccountWithMetadata): account is WalletWithMetadata {
    return (
        account.type === 'wallet' &&
        account.connectorType === 'injected' &&
        account.walletClientType !== 'privy'
    )
}
